/* Brand contact forms (Service request form, Contact form) — accessible
   client-side validation as progressive enhancement over Shopify's native
   contact form, plus the checklist dropdowns.

   Why validation runs on the submit BUTTON's click and in the capture phase,
   and why the form is never given `novalidate`:
   Shopify's spam protection (the captcha-bootstrap script in <head>) attaches
   a bubble-phase `submit` listener to every contact form, prevents the event,
   fetches a token and then calls form.submit() — which by spec bypasses both
   constraint validation and every submit listener. If an invalid form is ever
   allowed to fire a `submit` event, the captcha script posts it anyway and the
   page reloads with a server error. So we stop invalid submissions one step
   earlier: cancelling the button click cancels the submission algorithm before
   any `submit` event exists (the Enter key also arrives here — browsers
   synthesise a click on the default button), and a capture-phase listener on
   document runs before any form-level listener as a second line. Native
   validation is left enabled as the backstop for no-JS and for anything that
   slips past both. Nothing here changes what is submitted. */
(function () {
  'use strict';

  function fieldMessage(input, form) {
    if (input.validity.valueMissing) return form.dataset.errorRequired || 'This field is required.';
    if (input.type === 'tel' && (input.validity.patternMismatch || input.validity.tooLong)) {
      return form.dataset.errorPhone || 'Enter a valid phone number';
    }
    if (input.type === 'email' && (input.validity.typeMismatch || input.validity.patternMismatch)) {
      return form.dataset.errorEmail || 'Enter a valid email address.';
    }
    if (input.validity.rangeUnderflow || input.validity.rangeOverflow || input.validity.stepMismatch || input.validity.badInput) {
      return input.validationMessage;
    }
    return input.validationMessage || 'Please check this field.';
  }

  function labelFor(input) {
    var label = input.labels && input.labels[0];
    return label ? label.textContent.replace('*', '').trim() : input.name;
  }

  function setError(input, message) {
    var error = document.getElementById(input.id + '-error');
    input.setAttribute('aria-invalid', 'true');
    if (!error) return;
    input.setAttribute('aria-describedby', error.id);
    var text = error.querySelector('[data-error-text]') || error;
    text.textContent = message;
    error.hidden = false;
  }

  function clearError(input) {
    var error = document.getElementById(input.id + '-error');
    input.removeAttribute('aria-invalid');
    if (!error) return;
    if (input.getAttribute('aria-describedby') === error.id) input.removeAttribute('aria-describedby');
    error.hidden = true;
  }

  function inputsOf(form) {
    return Array.prototype.slice.call(form.querySelectorAll('.pa-form__input'));
  }

  /* Validates every field; renders inline errors + the summary; returns true
     when the form may be submitted. Idempotent — safe to run from both the
     click and the submit paths for the same attempt. */
  function validate(form) {
    var inputs = inputsOf(form);
    var summary = form.querySelector('[data-form-summary]');
    var summaryList = form.querySelector('[data-form-summary-list]');
    var invalid = inputs.filter(function (input) {
      return !input.checkValidity();
    });

    inputs.forEach(clearError);
    if (summary) {
      summary.hidden = true;
      if (summaryList) summaryList.innerHTML = '';
    }
    if (!invalid.length) return true;

    invalid.forEach(function (input) {
      var message = fieldMessage(input, form);
      setError(input, message);
      if (summaryList) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.href = '#' + input.id;
        a.className = 'link';
        a.textContent = labelFor(input) + ': ' + message;
        a.addEventListener('click', function (e) {
          e.preventDefault();
          input.focus();
        });
        li.appendChild(a);
        summaryList.appendChild(li);
      }
    });
    if (summary) {
      summary.hidden = false;
      summary.focus();
    } else {
      invalid[0].focus();
    }
    return false;
  }

  /* Checklist dropdown: a field-styled trigger opens the option grid; the
     ticked options are written back into the trigger so the choice stays
     visible when the panel is closed. Escape and outside clicks close it,
     focus returns to the trigger. Without JS the grid is simply open. */
  function enhanceChecklist(fieldset) {
    var toggle = fieldset.querySelector('[data-checklist-toggle]');
    var panel = fieldset.querySelector('[data-checklist-panel]');
    var summary = fieldset.querySelector('[data-checklist-summary]');
    if (!toggle || !panel) return;
    var placeholder = summary ? summary.dataset.placeholder || summary.textContent : '';

    var setOpen = function (open) {
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      fieldset.classList.toggle('is-open', open);
    };

    var updateSummary = function () {
      if (!summary) return;
      var picked = Array.prototype.slice
        .call(panel.querySelectorAll('input[type="checkbox"]:checked'))
        .map(function (box) {
          var text = box.closest('label') && box.closest('label').querySelector('.pa-form__check-text');
          return text ? text.textContent.trim() : box.value;
        });
      summary.textContent = picked.length ? picked.join(', ') : placeholder;
      fieldset.classList.toggle('is-filled', picked.length > 0);
    };

    setOpen(false);
    updateSummary();

    toggle.addEventListener('click', function () {
      setOpen(panel.hidden);
    });
    panel.addEventListener('change', updateSummary);
    fieldset.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !panel.hidden) {
        event.preventDefault();
        setOpen(false);
        toggle.focus();
      }
    });
    document.addEventListener('click', function (event) {
      if (!panel.hidden && !fieldset.contains(event.target)) setOpen(false);
    });
    // Back/forward cache and autofill restore ticks after load; keep the trigger honest.
    window.addEventListener('pageshow', updateSummary);
  }

  /* "Other" text box follows its checkbox: hidden until ticked, cleared when
     unticked. One per checklist; the box is found through aria-controls. */
  function enhanceOther(toggle) {
    var field = document.getElementById(toggle.getAttribute('aria-controls') || '');
    if (!field) return;
    var input = field.querySelector('input');
    var sync = function (focus) {
      field.hidden = !toggle.checked;
      toggle.setAttribute('aria-expanded', toggle.checked ? 'true' : 'false');
      if (!toggle.checked && input) input.value = '';
      if (toggle.checked && focus && input) input.focus();
    };
    sync(false);
    toggle.addEventListener('change', function () {
      sync(true);
    });
  }

  /* Phone fields take digits only: typed non-digits are blocked, and pasted or
     autofilled values (e.g. "+1 (865) 250-2569") are reduced to their digits.
     The 15-digit cap lives here, not in maxlength, because maxlength would cut
     a formatted paste before the clean-up runs. */
  var PHONE_MAX_DIGITS = 15;
  function enhanceDigitsOnly(input) {
    input.addEventListener('beforeinput', function (event) {
      // Block keystrokes with no digit in them. A multi-character insert that
      // does contain digits (autofill, keyboard suggestions) goes through and is
      // reduced to its digits by clean().
      if (event.data && !/\d/.test(event.data) && /^insert(Text|CompositionText)$/.test(event.inputType)) event.preventDefault();
    });
    var clean = function () {
      var value = input.value;
      var digits = value.replace(/\D/g, '').slice(0, PHONE_MAX_DIGITS);
      if (digits === value) return;
      var caret = input.selectionStart == null ? digits.length : value.slice(0, input.selectionStart).replace(/\D/g, '').length;
      input.value = digits;
      if (document.activeElement === input) {
        try { input.setSelectionRange(caret, caret); } catch (e) { /* not supported for this type */ }
      }
    };
    input.addEventListener('input', clean);
    clean();
  }

  function enhance(form) {
    Array.prototype.forEach.call(form.querySelectorAll('input[data-digits-only]'), enhanceDigitsOnly);
    inputsOf(form).forEach(function (input) {
      input.addEventListener('input', function () {
        if (input.getAttribute('aria-invalid') === 'true' && input.checkValidity()) clearError(input);
      });
      input.addEventListener('blur', function () {
        if (!input.checkValidity()) setError(input, fieldMessage(input, form));
      });
    });

    // First line: cancel the click, and no submission algorithm ever starts.
    Array.prototype.forEach.call(form.querySelectorAll('button[type="submit"], input[type="submit"]'), function (button) {
      button.addEventListener('click', function (event) {
        if (!validate(form)) event.preventDefault();
      });
    });

    Array.prototype.forEach.call(form.querySelectorAll('[data-checklist]'), enhanceChecklist);
    Array.prototype.forEach.call(form.querySelectorAll('[data-other-toggle]'), enhanceOther);
  }

  // Second line: capture phase on document runs before the captcha script's
  // form-level listener no matter who registered first; stopping propagation
  // keeps an invalid submit from ever reaching it.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.classList.contains('pa-form')) return;
      if (!validate(form)) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true
  );

  function init() {
    document.querySelectorAll('form.pa-form').forEach(function (form) {
      if (form.dataset.paEnhanced) return;
      form.dataset.paEnhanced = 'true';
      enhance(form);
    });
  }

  init();
  document.addEventListener('shopify:section:load', init);
})();
